export type RconStats = {
  online: boolean
  playersOnline?: number
  playersMax?: number | null
  players?: string[]
  /** Liste des pseudos réservée aux comptes connectés (sauf publicRconPlayerList). */
  playersListRestricted?: boolean
  updatedAt?: string
  error?: string
}

/** Masque les erreurs réseau RCON (serveur arrêté) — l’UI affiche « Hors ligne ». */
export function normalizeRconStatsError (error?: string): string | undefined {
  if (!error?.trim()) return undefined
  if (/ECONNREFUSED|ETIMEDOUT|ENOTFOUND|ECONNRESET|EHOSTUNREACH/i.test(error)) return undefined
  if (/^connect\s/i.test(error)) return undefined
  return error
}

function sanitizeRconStats (data: RconStats | null): RconStats | null {
  if (!data) return data
  if (data.online) return data
  const error = normalizeRconStatsError(data.error)
  if (error === data.error) return data
  const { error: _drop, ...rest } = data
  return error ? { ...rest, error } : rest
}

export async function fetchRconStats (signal?: AbortSignal): Promise<RconStats | null> {
  try {
    const res = await fetch('/api/rcon/stats', { signal, credentials: 'include' })
    const data = await res.json() as RconStats & { error?: string }
    if (!res.ok) {
      return sanitizeRconStats({
        online: false,
        error: data.error ?? res.statusText,
        updatedAt: data.updatedAt,
      })
    }
    return sanitizeRconStats(data)
  } catch (err) {
    if (signal?.aborted) return null
    return sanitizeRconStats({
      online: false,
      error: err instanceof Error ? err.message : String(err),
    })
  }
}

export function formatStatsDate (iso?: string) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}
